import type { RequestTiming } from '../api/httpClient'
import type { DetailOptions, MarkOptions, SaleTrace } from './saleTrace'
import type { TraceDetailKind, TraceStage } from './types'

/**
 * Трассировка продажи для кода продажи и transport: одна точка входа, не требующая ни стора, ни провайдера.
 *
 * Приложение при сборке стора подключает рабочий экземпляр (`installSaleTrace`); пока он не подключён — и в тестах, которым
 * трассировка не нужна, — каждый вызов ничего не делает. Такая форма нужна, чтобы метка в цикле продажи была ОДНОЙ строкой и
 * не тянула за собой зависимостей: ни одна метка не вправе повлиять на продажу.
 */
let active: SaleTrace | null = null

/**
 * Подключить экземпляр трассировки (`null` — отключить). Возвращает «отключатель» ровно этого экземпляра: стор, остановленный
 * позже соседа (тесты поднимают несколько сторов), не отключает чужую трассировку.
 */
export function installSaleTrace(instance: SaleTrace | null): () => void {
  active = instance
  return () => {
    if (active === instance) active = null
  }
}

export const trace = {
  /** Контрольная точка продажи (`click`, `pay-request`, …). `done` отправляет накопленное. */
  mark(stage: TraceStage, options: MarkOptions): void {
    active?.mark(stage, options)
  },
  /** Есть право на трассировку этого источника — собирать; нет — не собирать вовсе. */
  allow(scope: string, allowed: boolean): void {
    active?.allow(scope, allowed)
  },
  setDeep(enabled: boolean): void {
    active?.setDeep(enabled)
  },
  isDeep(): boolean {
    return active?.isDeep() ?? false
  },
  /** Событие подробного режима: очередь корзины, опрос, живой канал. */
  detail(kind: Exclude<TraceDetailKind, 'http'>, route: string, options?: DetailOptions): void {
    active?.detail(kind, route, options)
  },
  /** Вызывает transport на каждом запросе (подробный режим); в лёгком режиме ничего не пишет. */
  observeRequest(timing: RequestTiming): void {
    active?.observeRequest(timing)
  },
  flush(saleId: string): Promise<void> {
    return active?.flush(saleId) ?? Promise.resolve()
  },
  /** Связь вернулась — доставить то, что не дошло. */
  retryQueued(): Promise<void> {
    return active?.retryQueued() ?? Promise.resolve()
  },
}
